import { MaxNumbersOfSuggestions } from 'constants/suggestion';
import { useMemo } from 'react';
import {
    ContentSuggestion,
    ContentSuggestionParams,
    SuggestionsType,
} from '../types';
import { LabelSuggestionRequiredFields } from './useLabels';
import { PackSuggestionRequiredFields } from './usePacks';

type ContentSuggestionRequiredFields =
    | LabelSuggestionRequiredFields
    | PackSuggestionRequiredFields;

export const useContentSuggestions = ({
    contents,
    searchInput,
    isLoading,
    hasError,
    showOnlyFilterSuggestions,
    hasActiveSearch,
    suggestionParams,
    suggestionType,
}: {
    contents: ContentSuggestionRequiredFields[];
    searchInput?: string;
    hasActiveSearch: boolean;
    isLoading: boolean;
    hasError: boolean;
    showOnlyFilterSuggestions: boolean;
    suggestionParams: ContentSuggestionParams;
    suggestionType: SuggestionsType.Label | SuggestionsType.Pack;
}) => {
    const suggestedContent = useMemo((): ContentSuggestion[] => {
        if (
            !searchInput ||
            hasActiveSearch ||
            isLoading ||
            hasError ||
            showOnlyFilterSuggestions
        ) {
            return [];
        }

        const filtered = contents.filter(
            (content) =>
                content.slug &&
                content.name
                    ?.toLocaleLowerCase()
                    .startsWith(searchInput.toLocaleLowerCase()),
        );

        const sliced = filtered.slice(0, MaxNumbersOfSuggestions);

        const suggestions: ContentSuggestion[] = sliced.map(
            (content: ContentSuggestionRequiredFields): ContentSuggestion => [
                [
                    suggestionParams,
                    {
                        slug: content.slug,
                        name: content.name,
                        artworkUrl: content.artworkUrl,
                    },
                ],
                suggestionType,
            ],
        );

        return suggestions;
    }, [
        searchInput,
        hasActiveSearch,
        showOnlyFilterSuggestions,
        isLoading,
        hasError,
        contents,
        suggestionParams,
        suggestionType,
    ]);

    return suggestedContent;
};
